import { Capacitor, registerPlugin } from "@capacitor/core";
import { SUB_MONTHLY_PRICE_USD } from "src/config/products.config";

// MOCK_MODE: Set REACT_APP_MOCK_IAP=true in .env to test IAP flow without real billing
const MOCK_MODE = process.env.REACT_APP_MOCK_IAP === "true";

// Canonical product IDs used across frontend/backend/Stripe.
// Apple Store product IDs can differ (e.g. *_v1) and are mapped here.
const APPLE_PRODUCT_ID_MAP: Record<string, string> = {
  league_connect_basic_monthly: process.env.REACT_APP_APPLE_PRODUCT_ID_BASIC_MONTHLY || "league_connect_basic_monthly_v1",
  league_connect_monthly: process.env.REACT_APP_APPLE_PRODUCT_ID_MONTHLY || "league_connect_monthly",
  league_connect_annual_pro: process.env.REACT_APP_APPLE_PRODUCT_ID_ANNUAL_PRO || "league_connect_annual_pro",
  league_connect_season_pass: process.env.REACT_APP_APPLE_PRODUCT_ID_SEASON_PASS || "league_connect_season_pass",
  league_connect_boost_starter: process.env.REACT_APP_APPLE_PRODUCT_ID_BOOST_STARTER || "league_connect_boost_starter",
  league_connect_boost_power: process.env.REACT_APP_APPLE_PRODUCT_ID_BOOST_POWER || "league_connect_boost_power",
};

// Reverse map for canonical product IDs to Apple Store product IDs
const APPLE_PRODUCT_ID_REVERSE_MAP: Record<string, string> = Object.entries(APPLE_PRODUCT_ID_MAP).reduce(
  (acc, [canonicalId, appleStoreId]) => {
    acc[appleStoreId] = canonicalId;
    return acc;
  },
  {} as Record<string, string>,
);

// Convert canonical product ID to Apple Store product ID
const toStoreProductId = (canonicalProductId: string): string => {
  if (!isIosNative()) return canonicalProductId;
  return APPLE_PRODUCT_ID_MAP[canonicalProductId] || canonicalProductId;
};

// Convert Apple Store product ID to canonical product ID
const toCanonicalProductId = (storeProductId: string): string => {
  if (!isIosNative()) return storeProductId;
  return APPLE_PRODUCT_ID_REVERSE_MAP[storeProductId] || storeProductId;
};

type ProductInfo = {
  id: string;
  displayName: string;
  price: string;
};

type PurchaseResult =
  | {
      status: "success";
      transactionId: string; // iOS: transaction.id (stringified). Android: purchaseToken
      productId: string;
      jws: string;
      purchaseToken?: string; // Android only
      orderId?: string; // Android only
    }
  | {
      status: "pending";
    };

// Use Capacitor registerPlugin to obtain a proxy even if not present on window.Plugins
const IAP = registerPlugin<any>("IAPPlugin");

export const isIosNative = () => Capacitor.getPlatform() === "ios";
export const isAndroidNative = () => Capacitor.getPlatform() === "android";
export const isNativeIapSupported = () => isIosNative() || isAndroidNative();

// Fetch products metadata (useful to confirm availability before purchasing)
export const getProducts = async (productIds: string[]): Promise<ProductInfo[]> => {
  if (!isNativeIapSupported()) throw new Error("In-app purchases are only available on native mobile (iOS/Android).");
  const storeProductIds = productIds.map(toStoreProductId); // Convert canonical product IDs to Apple Store product IDs

  // MOCK MODE: Return fake product data for testing
  if (MOCK_MODE) {
    console.log("🎭 MOCK MODE: Returning fake products for", productIds);
    return productIds.map((id) => ({
      id,
      displayName: `Mock Product: ${id}`,
      price: `$${SUB_MONTHLY_PRICE_USD.toFixed(2)}`,
    }));
  }

  // REAL MODE: Call native plugin (works for both iOS and Android in production)
  const result = await IAP.getProducts({ productIds: storeProductIds });
  const products = result?.products ?? [];
  return products.map((product: ProductInfo) => ({
    ...product,
    id: toCanonicalProductId(product.id),
  }));
};

// Purchase the given productId using StoreKit 2 (iOS) or Google Play Billing (Android)
export const purchaseProduct = async (productId: string): Promise<PurchaseResult> => {
  if (!isNativeIapSupported()) throw new Error("In-app purchases are only available on native mobile (iOS/Android).");
  const storeProductId = toStoreProductId(productId); // Convert canonical product ID to Apple Store product ID

  // MOCK MODE: Simulate successful purchase for testing
  if (MOCK_MODE) {
    console.log("🎭 MOCK MODE: Simulating purchase success for", productId);
    await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate delay
    return {
      status: "success",
      transactionId: `mock_tx_${Date.now()}`,
      productId,
      jws: "mock_jws_signature",
      purchaseToken: `mock_token_${Date.now()}`, // For Android
      orderId: `mock_order_${Date.now()}`, // For Android
    };
  }

  // REAL MODE: Call native plugin (works for both iOS and Android in production)
  const purchaseResult = await IAP.purchase({ productId: storeProductId });
  if (purchaseResult?.status === "success") {
    return {
      ...purchaseResult,
      // Keep canonical ID for backend flows and existing business logic.
      productId,
    };
  }
  return purchaseResult;
};

// Restore current entitlements
export const restorePurchases = async () => {
  if (!isNativeIapSupported()) throw new Error("In-app purchases are only available on native mobile (iOS/Android).");

  // MOCK MODE: Return empty restore result
  if (MOCK_MODE) {
    console.log("🎭 MOCK MODE: Simulating restore (no purchases)");
    return { purchases: [] };
  }

  // REAL MODE: Call native plugin (works for both iOS and Android in production)
  return IAP.restore();
};

// Show native manage subscriptions screen
// iOS: Settings/StoreKit screen (iOS 15.0+)
// Android: Play Store subscriptions screen
export const showManageSubscriptions = async (): Promise<void> => {
  if (!isNativeIapSupported()) throw new Error("Manage subscriptions is only available on native mobile (iOS/Android).");

  // MOCK MODE: Just log, don't try to open native screen
  if (MOCK_MODE) {
    console.log("🎭 MOCK MODE: Would open manage subscriptions screen");
    return;
  }

  // REAL MODE: Call native plugin (works for both iOS and Android in production)
  await IAP.showManageSubscriptions();
};
